import { getEnv } from '../config';
const SESSION_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const TOKEN_EXPIRY_KEY = 'auth_token_expires_at';
const PKCE_VERIFIER_KEY = 'pkce_code_verifier';
const PKCE_STATE_KEY = 'pkce_state';
const REFRESH_BUFFER_MS = 5 * 60 * 1000;
const APS_AUTH_BASE_URLS = {
    dev: 'https://developer-dev.api.autodesk.com/authentication/v2',
    stg: 'https://developer-stg.api.autodesk.com/authentication/v2',
    prod: 'https://developer.api.autodesk.com/authentication/v2',
};
function generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
}
async function computeCodeChallenge(verifier) {
    const data = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(new Uint8Array(digest));
}
function generateOAuthState() {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
}
function base64UrlEncode(buffer) {
    let binary = '';
    for (const byte of buffer) {
        binary += String.fromCharCode(byte);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}
class AuthClientImpl {
    constructor() {
        this.currentState = { status: 'loading' };
        this.listeners = new Set();
        this.isInitialized = false;
        this.isLoginInProgress = false;
        this.authConfig = null;
        this._viewerMode = false;
        this.pendingReturnTo = null;
        this.refreshTimer = null;
    }
    consumePendingReturnTo() {
        const val = this.pendingReturnTo;
        this.pendingReturnTo = null;
        return val;
    }
    initialize(config) {
        if (this.isInitialized)
            return;
        this.isInitialized = true;
        if (!config.clientId) {
            const mockUser = {
                userId: 'dev-user-123',
                email: 'developer@autodesk.com',
                name: 'Local Developer',
                role: 'admin',
                actions: [
                    'READ',
                    'WRITE',
                    'SCHEDULE',
                    'VALIDATE',
                    'VALIDATE_EXCLUDE_LINEAGES',
                    'ROLLBACK',
                    'DELETE',
                ],
            };
            const mockTokens = {
                accessToken: 'mock-access-token',
                idToken: 'mock-id-token',
                expiresAt: Date.now() + 24 * 60 * 60 * 1000,
            };
            sessionStorage.setItem(SESSION_TOKEN_KEY, 'mock-access-token');
            this.setState({ status: 'authenticated', user: mockUser, tokens: mockTokens });
            return;
        }
        this.authConfig = config;
        const params = new URLSearchParams(window.location.search);
        if (params.has('code') || params.has('error')) {
            const callbackUrl = window.location.href;
            console.log('[AuthClient] OAuth callback detected, url:', callbackUrl);
            window.history.replaceState({}, '', '/');
            void this.handleOAuthCallback(callbackUrl);
        }
        else {
            void this.restoreSession();
        }
    }
    async handleOAuthCallback(callbackUrl) {
        const returnTo = sessionStorage.getItem('auth_return_to') || '/';
        sessionStorage.removeItem('auth_return_to');
        try {
            if (returnTo !== '/')
                this.pendingReturnTo = returnTo;
            await this.handleCallback(callbackUrl);
            window.history.replaceState({}, '', returnTo);
        }
        catch (err) {
            this.pendingReturnTo = null;
            const message = err instanceof Error ? err.message : String(err);
            console.error('[AuthClient] OAuth callback failed:', err);
            this.setState({ status: 'error', error: `Authentication failed: ${message}` });
            window.history.replaceState({}, '', '/');
        }
    }
    async restoreSession() {
        let token = localStorage.getItem(SESSION_TOKEN_KEY);
        if (!token) {
            this.setState({ status: 'unauthenticated' });
            return;
        }
        const expiresAt = Number(localStorage.getItem(TOKEN_EXPIRY_KEY) || '0');
        const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (expiresAt > 0 && Date.now() > expiresAt - REFRESH_BUFFER_MS) {
            if (!storedRefreshToken) {
                this.clearStoredTokens();
                this.setState({ status: 'unauthenticated' });
                return;
            }
            const refreshed = await this.tryRefreshSession(storedRefreshToken);
            if (!refreshed) {
                this.clearStoredTokens();
                this.setState({ status: 'unauthenticated' });
                return;
            }
            token = localStorage.getItem(SESSION_TOKEN_KEY);
        }
        const user = await this.fetchUserProfile(token);
        if (user) {
            const newExpiresAt = Number(localStorage.getItem(TOKEN_EXPIRY_KEY) || '0');
            this.setState({
                status: 'authenticated',
                user,
                tokens: {
                    accessToken: token,
                    idToken: '',
                    expiresAt: newExpiresAt,
                    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) ?? undefined,
                },
            });
            this.scheduleTokenRefresh(newExpiresAt);
        }
        else {
            this.clearStoredTokens();
            this.setState({ status: 'unauthenticated' });
        }
    }
    async tryRefreshSession(refreshToken) {
        try {
            const backendUrl = getEnv('API_BASE_URL') ||
                process.env.REACT_APP_API_BASE_URL ||
                'http://localhost:3000/dev/v1';
            const response = await fetch(`${backendUrl}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken }),
            });
            if (!response.ok)
                return false;
            const { data } = (await response.json());
            if (!data.accessToken || !data.expiresAt)
                return false;
            localStorage.setItem(SESSION_TOKEN_KEY, data.accessToken);
            if (data.refreshToken)
                localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
            if (data.expiresAt)
                localStorage.setItem(TOKEN_EXPIRY_KEY, String(data.expiresAt));
            return true;
        }
        catch (err) {
            console.warn('[AuthClient] Silent token refresh failed:', err);
            return false;
        }
    }
    scheduleTokenRefresh(expiresAt) {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
        if (expiresAt <= 0)
            return;
        const delay = expiresAt - Date.now() - REFRESH_BUFFER_MS;
        if (delay <= 0)
            return;
        this.refreshTimer = setTimeout(() => {
            void this.silentRefresh();
        }, delay);
    }
    async silentRefresh() {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken) {
            this.clearStoredTokens();
            this.setState({ status: 'unauthenticated' });
            return;
        }
        const refreshed = await this.tryRefreshSession(refreshToken);
        if (!refreshed) {
            this.clearStoredTokens();
            this.setState({ status: 'unauthenticated' });
            return;
        }
        const newToken = localStorage.getItem(SESSION_TOKEN_KEY);
        const newExpiresAt = Number(localStorage.getItem(TOKEN_EXPIRY_KEY) || '0');
        if (this.currentState.status === 'authenticated') {
            this.setState({
                status: 'authenticated',
                user: this.currentState.user,
                tokens: {
                    accessToken: newToken,
                    idToken: '',
                    expiresAt: newExpiresAt,
                    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) ?? undefined,
                },
            });
        }
        this.scheduleTokenRefresh(newExpiresAt);
    }
    clearStoredTokens() {
        localStorage.removeItem(SESSION_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
    }
    login() {
        if (!this.authConfig) {
            if (this.currentState.status !== 'authenticated') {
                const mockUser = {
                    userId: 'dev-user-123',
                    email: 'developer@autodesk.com',
                    name: 'Local Developer',
                    role: 'admin',
                    actions: [
                        'READ',
                        'WRITE',
                        'SCHEDULE',
                        'VALIDATE',
                        'VALIDATE_EXCLUDE_LINEAGES',
                        'ROLLBACK',
                        'DELETE',
                    ],
                };
                const mockTokens = {
                    accessToken: 'mock-access-token',
                    idToken: 'mock-id-token',
                    expiresAt: Date.now() + 24 * 60 * 60 * 1000,
                };
                sessionStorage.setItem(SESSION_TOKEN_KEY, 'mock-access-token');
                this.setState({ status: 'authenticated', user: mockUser, tokens: mockTokens });
            }
            return;
        }
        if (this.isLoginInProgress)
            return;
        this.isLoginInProgress = true;
        void this.startPkceLogin().catch((err) => {
            this.isLoginInProgress = false;
            const message = err instanceof Error ? err.message : String(err);
            this.setState({ status: 'error', error: `Login failed: ${message}` });
        });
    }
    async startPkceLogin() {
        const { clientId, redirectUri, scope, env } = this.authConfig;
        const verifier = generateCodeVerifier();
        const challenge = await computeCodeChallenge(verifier);
        const state = generateOAuthState();
        sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
        sessionStorage.setItem(PKCE_STATE_KEY, state);
        const baseUrl = APS_AUTH_BASE_URLS[env] ?? APS_AUTH_BASE_URLS.stg;
        const authParams = new URLSearchParams({
            response_type: 'code',
            client_id: clientId,
            redirect_uri: redirectUri,
            scope,
            state,
            code_challenge: challenge,
            code_challenge_method: 'S256',
        });
        window.location.assign(`${baseUrl}/authorize?${authParams.toString()}`);
    }
    async handleCallback(callbackUrl) {
        const url = new URL(callbackUrl ?? window.location.href);
        const code = url.searchParams.get('code');
        if (!code)
            throw new Error('No authorization code in callback URL');
        const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
        sessionStorage.removeItem(PKCE_VERIFIER_KEY);
        sessionStorage.removeItem(PKCE_STATE_KEY);
        if (!codeVerifier)
            throw new Error('No PKCE code verifier found in sessionStorage');
        const redirectUri = this.authConfig?.redirectUri ?? window.location.origin + '/';
        const backendUrl = getEnv('API_BASE_URL') ||
            process.env.REACT_APP_API_BASE_URL ||
            'http://localhost:3000/dev/v1';
        console.log('[AuthClient] handleCallback: exchanging code via backend BFF', { backendUrl });
        const response = await fetch(`${backendUrl}/auth/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, codeVerifier, redirectUri }),
        });
        if (!response.ok) {
            const errBody = (await response.json().catch(() => ({})));
            throw new Error(`Backend token exchange failed (${response.status}): ${String(errBody.error ?? '')}`);
        }
        const { data } = (await response.json());
        if (!data.accessToken)
            throw new Error('No access token returned from backend');
        localStorage.setItem(SESSION_TOKEN_KEY, data.accessToken);
        if (data.refreshToken)
            localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        if (data.expiresAt)
            localStorage.setItem(TOKEN_EXPIRY_KEY, String(data.expiresAt));
        const user = await this.fetchUserProfile(data.accessToken);
        if (!user)
            throw new Error('Failed to fetch user profile from backend');
        console.log('[AuthClient] fetched user:', user);
        const expiresAt = data.expiresAt ?? 0;
        this.setState({
            status: 'authenticated',
            user,
            tokens: {
                accessToken: data.accessToken,
                idToken: '',
                expiresAt,
                refreshToken: data.refreshToken,
            },
        });
        this.scheduleTokenRefresh(expiresAt);
    }
    logout() {
        this.clearStoredTokens();
        sessionStorage.removeItem(SESSION_TOKEN_KEY);
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
        this.setState({ status: 'unauthenticated' });
        if (this.authConfig) {
            const { env } = this.authConfig;
            const baseUrl = APS_AUTH_BASE_URLS[env] ?? APS_AUTH_BASE_URLS.stg;
            const postLogoutUri = encodeURIComponent(window.location.origin);
            window.location.assign(`${baseUrl}/logout?post_logout_redirect_uri=${postLogoutUri}`);
        }
    }
    getAccessToken() {
        if (this.currentState.status !== 'authenticated')
            return null;
        return localStorage.getItem(SESSION_TOKEN_KEY) ?? sessionStorage.getItem(SESSION_TOKEN_KEY);
    }
    getUser() {
        if (this.currentState.status !== 'authenticated')
            return null;
        return this.currentState.user;
    }
    getState() {
        return this.currentState;
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    async fetchUserProfile(token) {
        try {
            const backendUrl = getEnv('API_BASE_URL') ||
                process.env.REACT_APP_API_BASE_URL ||
                'http://localhost:3000/dev/v1';
            const response = await fetch(`${backendUrl}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok)
                return null;
            const { data } = (await response.json());
            return data ?? null;
        }
        catch {
            return null;
        }
    }
    setViewerMode(enabled) {
        if (this.currentState.status !== 'authenticated' || this.currentState.user.role !== 'admin') {
            return;
        }
        this._viewerMode = enabled;
        this.setState(this.currentState);
    }
    setState(state) {
        if (state.status === 'authenticated') {
            this.currentState = { ...state, viewerMode: this._viewerMode };
        }
        else {
            this._viewerMode = false;
            this.currentState = state;
        }
        this.listeners.forEach((listener) => listener(this.currentState));
    }
}
export const AuthClient = new AuthClientImpl();
export function initializeAuth() {
    const apsEnv = getEnv('APS_ENVIRONMENT') || process.env.REACT_APP_APS_ENVIRONMENT;
    const env = apsEnv === 'prod' ? 'prod' : apsEnv === 'dev' ? 'dev' : 'stg';
    const config = {
        clientId: getEnv('AUTH_CLIENT_ID') || process.env.REACT_APP_AUTH_CLIENT_ID || '',
        redirectUri: window.location.origin + '/',
        scope: 'data:read data:write',
        env,
    };
    AuthClient.initialize(config);
}
